import * as React from "react";
import { useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import TwoWheelerOutlinedIcon from "@mui/icons-material/TwoWheelerOutlined";
import { getVehiclesBySearch } from "modules/orderManagement/action";
import { BRAND } from "themes";

interface Props {
  companyId: string;
  vehicleType?: string;
  onSelectVehicle: (vehicle: any | null) => void;
}

const getLabel = (option: any) => {
  if (!option) return "";
  if (typeof option === "string") return option;
  return (
    option.name ||
    option.vehicleName ||
    option.model ||
    option.modelName ||
    option.title ||
    option.vehicleId ||
    option.id ||
    option._id ||
    ""
  );
};

/** API: vehicleType 0 = Car, 1 = Bike */
const isBike = (option: any) =>
  String(option?.vehicleType) === "1" ||
  option?.vehicleTypeLabel === "Bike";

const VehicleTypeIcon = ({
  option,
  size = 20,
}: {
  option: any;
  size?: number;
}) => {
  const bike = isBike(option);
  const Icon = bike ? TwoWheelerOutlinedIcon : DirectionsCarOutlinedIcon;
  return (
    <Icon
      sx={{
        fontSize: size,
        color: bike ? BRAND.secondary : BRAND.primary,
        flexShrink: 0,
      }}
      aria-label={bike ? "Bike" : "Car"}
    />
  );
};

export default function VehiclesDropdown({
  companyId,
  vehicleType = "0",
  onSelectVehicle,
}: Props) {
  const dispatch: any = useDispatch();

  const [search, setSearch] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [options, setOptions] = React.useState<any[]>([]);
  const [value, setValue] = React.useState<any>(null);
  const requestIdRef = React.useRef(0);

  React.useEffect(() => {
    setValue(null);
    setSearch("");
    setOptions([]);
    onSelectVehicle(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  React.useEffect(() => {
    if (!companyId) {
      setOptions([]);
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);

    dispatch(
      getVehiclesBySearch(
        search,
        companyId,
        (result: any[]) => {
          if (requestId !== requestIdRef.current) {
            return;
          }
          const nextOptions = Array.isArray(result) ? result : [];
          setOptions(nextOptions);
          setLoading(false);
        },
        vehicleType,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, vehicleType, search]);

  return (
    <Autocomplete
      fullWidth
      options={options}
      loading={loading}
      value={value}
      disabled={!companyId}
      filterOptions={(opts) => opts}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(option, selected) =>
        Boolean(
          selected &&
            (option?.vehicleId === selected?.vehicleId ||
              option?.id === selected?.id ||
              option?._id === selected?._id),
        )
      }
      onChange={(_, selected) => {
        setValue(selected);
        setSearch(selected ? getLabel(selected) : "");
        onSelectVehicle(selected);
      }}
      inputValue={value ? getLabel(value) : search}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "input") {
          setValue(null);
          onSelectVehicle(null);
          setSearch(newInputValue);
        } else if (reason === "clear") {
          setValue(null);
          onSelectVehicle(null);
          setSearch("");
        }
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props as typeof props & {
          key?: React.Key;
        };
        const bike = isBike(option);
        return (
          <Box
            component="li"
            key={key || option?.vehicleId || option?.id || option?._id}
            {...optionProps}
            sx={{
              display: "flex !important",
              alignItems: "center",
              gap: 1.25,
            }}
          >
            <VehicleTypeIcon option={option} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box
                component="span"
                sx={{
                  display: "block",
                  fontWeight: 600,
                  color: BRAND.ink,
                  lineHeight: 1.3,
                }}
              >
                {getLabel(option) || "Unnamed vehicle"}
              </Box>
              <Box
                component="span"
                sx={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 500,
                  color: BRAND.muted,
                  lineHeight: 1.3,
                }}
              >
                {bike ? "Bike" : "Car"}
                {option?.carTypeName || option?.carType
                  ? ` · ${option.carTypeName || option.carType}`
                  : ""}
              </Box>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label="Select Vehicle"
          InputProps={{
            ...params.InputProps,
            startAdornment: value ? (
              <InputAdornment position="start" sx={{ ml: 0.5, mr: 0 }}>
                <VehicleTypeIcon option={value} size={22} />
              </InputAdornment>
            ) : (
              params.InputProps.startAdornment
            ),
          }}
        />
      )}
    />
  );
}
